import { useRef } from "react";
import { useRouter } from "next/router";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale } from "chart.js/auto";
import Annotation from "chartjs-plugin-annotation";

import {
  ColorAA,
  getColorsForDataset,
  useAATheme,
} from "@bagel-web/components";
import { AgentCloneTree } from "../types";
import { getAncestorAgents, getCurrentAgent } from "./AgentCloneHistory";
import {
  EMPTY_SESSION_WIDTH,
  getDefaultChartOptions,
} from "../reports/config/cloneTimeline";
import { TimelineContainer } from "../session/CloneTimeline";

ChartJS.register(CategoryScale);
ChartJS.register(Annotation);

type TimelineAgent = {
  session_id: string;
  start?: number | null;
  end?: number | null;
};

const AgentCloneTimeline = ({
  sessionId,
  agentId,
  cloneTree,
}: {
  sessionId: string;
  agentId: string;
  cloneTree: AgentCloneTree;
}) => {
  const chartRef = useRef();
  const router = useRouter();

  const { isDarkMode } = useAATheme();

  // Place ancestor sessions in separate datasets to enable bar stacking.
  const ancestorAgents: Array<TimelineAgent> = [];
  const agents: Array<TimelineAgent> = [];

  ancestorAgents.push(
    ...(getAncestorAgents(cloneTree)?.map((parent) => {
      const start = parent.moment_id;
      const end = parent.children?.[0]
        ? parent.children?.[0]?.moment_id
        : start + EMPTY_SESSION_WIDTH;

      return {
        session_id: parent.session_id,
        start,
        end,
      };
    }) || [])
  );

  const currentAgent = getCurrentAgent(cloneTree);

  if (currentAgent) {
    agents.push({
      session_id: sessionId,
      start: currentAgent?.moment_id,
      end: currentAgent?.children?.reduce(
        (max, child) => Math.max(max, child.moment_id),
        currentAgent?.moment_id + EMPTY_SESSION_WIDTH
      ),
    });
  }

  const addChildSessions = (children?: Array<AgentCloneTree>) => {
    children?.forEach((child) => {
      agents.push({
        session_id: child.session_id,
        start: child.moment_id,
        end: child.children?.reduce(
          (max, grandchild) => Math.max(max, grandchild.moment_id),
          child.moment_id + EMPTY_SESSION_WIDTH
        ),
      });

      if (child.children) {
        addChildSessions(child.children);
      }
    });
  };

  addChildSessions(currentAgent?.children);

  if (!currentAgent && agents.length === 0 && ancestorAgents.length === 0) {
    return null;
  }

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (chartRef.current) {
      const element = getElementAtEvent(chartRef.current, e);
      const index = element?.[0]?.index;
      const datasetIndex = element?.[0]?.datasetIndex;

      if (datasetIndex === 0 && index !== undefined && agents[index]) {
        router.push(`/sessions/${agents[index].session_id}/agent/${agentId}`);
      } else {
        const ancestorIndex = datasetIndex ? datasetIndex - 1 : undefined;
        if (ancestorIndex !== undefined && ancestorAgents[ancestorIndex]) {
          router.push(
            `/sessions/${ancestorAgents[ancestorIndex].session_id}/agent/${agentId}`
          );
        }
      }
    }
  };

  const ancestorStyles = getColorsForDataset(3, isDarkMode);
  const currentSessionStyles = getColorsForDataset(0, isDarkMode);
  const childStyles = getColorsForDataset(1, isDarkMode);

  const datasets = [
    // Current session and child sessions in one dataset to not stack
    {
      data: agents.map((agent) => [agent.start, agent.end]),
      borderColor: agents.map((_, index) =>
        index === 0 ? currentSessionStyles.borderColor : childStyles.borderColor
      ),
      backgroundColor: agents.map((_, index) =>
        index === 0
          ? currentSessionStyles.backgroundColor
          : childStyles.backgroundColor
      ),
      borderWidth: 2,
      borderSkipped: false,
      label: "sessions",
    },
    // Ancestors in separate datasets each to stack
    ...ancestorAgents.map((agent) => ({
      data: [[agent.start, agent.end]],
      borderColor: ancestorStyles.borderColor,
      backgroundColor: ancestorStyles.backgroundColor,
      borderWidth: 2,
      borderSkipped: false,
      label: "ancestor",
    })),
  ];

  const options = getDefaultChartOptions({ isDarkMode });
  const legendTextColor = isDarkMode ? ColorAA.lightBeige : ColorAA.lightBlack;
  options.plugins!.legend!.labels!.generateLabels = () => {
    return [
      ...(ancestorAgents.length > 0
        ? [
            {
              text: "Ancestor Agents",
              fillStyle: ancestorStyles.backgroundColor,
              strokeStyle: ancestorStyles.borderColor,
              fontColor: legendTextColor,
              font: { size: 14 },
            },
          ]
        : []),
      {
        text: "Current Agent",
        fillStyle: currentSessionStyles.backgroundColor,
        strokeStyle: currentSessionStyles.borderColor,
        fontColor: legendTextColor,
        font: { size: 14 },
      },
      ...(agents.length > 1
        ? [
            {
              text: "Child Agents",
              fillStyle: childStyles.backgroundColor,
              strokeStyle: childStyles.borderColor,
              fontColor: legendTextColor,
              font: { size: 14 },
            },
          ]
        : []),
    ];
  };
  options.plugins!.tooltip!.callbacks!.title = (context) => {
    if (
      context[0]?.datasetIndex !== undefined &&
      context[0]?.dataset.label === "ancestor"
    ) {
      const ancestorIndex = context[0].datasetIndex - 1;
      return ancestorIndex >= 0
        ? ancestorAgents[ancestorIndex]?.session_id
        : undefined;
    }
  };

  return (
    <TimelineContainer style={{ height: `${agents.length * 30 + 100}px` }}>
      <Bar
        ref={chartRef}
        onClick={handleClick}
        data={{
          labels: agents.map((agent) => agent.session_id),
          datasets: datasets,
        }}
        options={options}
      />
    </TimelineContainer>
  );
};

export default AgentCloneTimeline;
